import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Ticket, X, Sparkles, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function RedeemCodeModal({ open, onClose, onRedeemed, defaultEpisode = null }) {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [episode, setEpisode] = useState(defaultEpisode ? String(defaultEpisode) : "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const doPreview = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/access-codes/preview", { code: code.trim() });
      setPreview(data);
      if (!data.valid) toast.error(data.reason || "code not valid");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "invalid code");
      setPreview(null);
    } finally { setBusy(false); }
  };

  const doRedeem = async () => {
    if (!code.trim()) return;
    if (!user) return;
    setBusy(true);
    try {
      const payload = { code: code.trim() };
      if (preview?.scope === "any") {
        if (!episode) { toast.error("pick an episode number to unlock"); setBusy(false); return; }
        payload.episode_number = parseInt(episode, 10);
      }
      const { data } = await api.post("/access-codes/redeem", payload);
      const n = (data.unlocked_episode_numbers || []).length;
      if (data.scope === "all") toast.success("♡ full library unlocked!");
      else if (n > 0) toast.success(`♡ unlocked ${n} episode${n === 1 ? "" : "s"}!`);
      else toast.success("already unlocked");
      onRedeemed?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "redeem failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="redeem-modal">
      <div className="bg-coal border-2 border-hotpink max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-hotpink" data-testid="redeem-close"><X size={20}/></button>
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="text-hotpink" size={22} />
          <h2 className="brush-title text-3xl text-white">GOT A CODE?</h2>
        </div>
        {!user && (
          <div className="bg-hotpink/10 border border-hotpink/40 p-3 mb-4 text-sm font-outfit text-white/80 flex items-center gap-3">
            <LogIn size={16} className="text-hotpink"/>
            <span>you need an account to redeem — <Link to="/login" className="text-hotpink underline" onClick={onClose}>log in</Link> or <Link to="/signup" className="text-hotpink underline" onClick={onClose}>sign up</Link> first.</span>
          </div>
        )}
        <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">gift code</label>
        <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setPreview(null); }} placeholder="LAUNCH2026" maxLength={32} data-testid="redeem-code-input" autoFocus className="w-full bg-ink border border-white/15 px-3 py-3 text-white font-outfit text-lg tracking-widest focus:border-hotpink focus:outline-none uppercase"/>
        {preview?.valid && (
          <div className="mt-3 bg-ink border border-hotpink/40 p-3 text-sm font-outfit text-white/80" data-testid="redeem-preview">
            <div className="text-hotpink text-[10px] uppercase tracking-widest mb-1">this code unlocks</div>
            {preview.scope === "episode" && <div>Episode {preview.episode_number}</div>}
            {preview.scope === "any" && <div>Any single episode (you pick)</div>}
            {preview.scope === "all" && <div>The full library — every episode ♡</div>}
            {preview.note && <div className="text-white/50 text-xs mt-1">&ldquo;{preview.note}&rdquo;</div>}
          </div>
        )}
        {preview?.valid && preview.scope === "any" && (
          <div className="mt-3">
            <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">which episode?</label>
            <input value={episode} onChange={(e) => setEpisode(e.target.value)} type="number" min="1" placeholder="episode #" data-testid="redeem-episode-input" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none"/>
          </div>
        )}
        <div className="mt-4 flex items-center gap-2">
          {!preview?.valid && (
            <button onClick={doPreview} disabled={busy || !code.trim()} data-testid="redeem-check" className="flex-1 bg-white/10 hover:bg-white/20 text-white font-outfit font-bold uppercase tracking-widest text-xs px-4 py-3 disabled:opacity-60">
              check code
            </button>
          )}
          {preview?.valid && (
            <button onClick={doRedeem} disabled={busy || !user} data-testid="redeem-confirm" className="flex-1 bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-4 py-3 disabled:opacity-60 flex items-center justify-center gap-2">
              <Sparkles size={14}/> {busy ? "..." : "redeem"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
